import { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Activity, AlertCircle, CheckCircle2, Clock, Shield, UserCircle, Mail, Phone, Linkedin } from 'lucide-react';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

function CustomerDashboard() {
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [user, setUser] = useState(null);
  const [securityStatus, setSecurityStatus] = useState(null);
  const [accountManager, setAccountManager] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const backendUrl = getBackendUrl();
        const [subsRes, ticketsRes, userRes, statusRes, managerRes] = await Promise.all([
          fetch(`${backendUrl}/api/subscriptions/my`, { credentials: 'include' }),
          fetch(`${backendUrl}/api/support/tickets`, { credentials: 'include' }),
          fetch(`${backendUrl}/api/auth/me`, { credentials: 'include' }),
          fetch(`${backendUrl}/api/nist-security/my`, { credentials: 'include' }),
          fetch(`${backendUrl}/api/my-account-manager`, { credentials: 'include' })
        ]);

        if (subsRes.ok) {
          const subsData = await subsRes.json();
          setSubscriptions(subsData);
        }

        if (ticketsRes.ok) {
          const ticketsData = await ticketsRes.json();
          setTickets(ticketsData.tickets || []);
        }

        if (userRes.ok) {
          const userData = await userRes.json();
          setUser(userData);
        }

        if (statusRes.ok) {
          const statusData = await statusRes.json();
          setSecurityStatus(statusData);
        }

        if (managerRes.ok) {
          const managerData = await managerRes.json();
          setAccountManager(managerData.account_manager);
        }
      } catch (error) {
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Count open tickets from Zoho Desk (status is not 'Closed' or 'Resolved')
  const openTickets = tickets.filter(t => {
    const status = (t.status || '').toLowerCase();
    return status !== 'closed' && status !== 'resolved';
  }).length;
  const activeSubs = subscriptions.filter(s => s.status === 'active').length;

  if (loading) {
    return (
      <DashboardLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Dashboard">
      <div className="space-y-6" data-testid="customer-dashboard">
        {/* Active Subscription Card */}
        {subscriptions.length > 0 && subscriptions[0].status === 'active' && (
          <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-6" data-testid="active-subscription-card">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Activity className="h-6 w-6 text-primary" />
                  <h3 className="text-2xl font-mono font-bold text-primary">
                    Cyber {subscriptions[0].tier.toUpperCase()}
                  </h3>
                  <span className="px-3 py-1 rounded-full text-xs font-mono bg-primary/20 text-primary">
                    ACTIVE
                  </span>
                </div>
                <p className="text-sm text-muted-foreground font-mono">
                  {(subscriptions[0].active_products || []).length} active products
                </p>
              </div>
            </div>
            
            {(subscriptions[0].active_products || []).length === 0 ? (
              <div className="text-center py-6 bg-background/50 rounded-md border border-border">
                <p className="text-muted-foreground font-mono text-sm">No products currently active</p>
                <p className="text-xs text-muted-foreground mt-1">Contact your administrator</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {(subscriptions[0].active_products || []).map((product, index) => (
                  <div key={index} className="flex items-center gap-2 bg-background/50 backdrop-blur-sm rounded-md p-3 border border-border">
                    <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                    <span className="text-xs font-mono">{product}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Security Status (NIST) Card - Prominent */}
        {securityStatus && (
          <div className="bg-gradient-to-br from-card via-card to-primary/5 border-2 border-primary/30 rounded-lg p-6 shadow-[0_0_30px_rgba(0,248,179,0.15)]" data-testid="security-status-dashboard-card">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <Shield className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-mono font-bold">Security Status (NIST)</h3>
                    <p className="text-xs text-muted-foreground font-mono">Based on NIST Cybersecurity Framework</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground font-mono mb-1">Overall</p>
                    <div className="flex items-center gap-2">
                      <p className={`text-4xl font-mono font-bold ${
                        securityStatus.overall_score >= 80 ? 'text-primary' :
                        securityStatus.overall_score >= 60 ? 'text-accent' :
                        securityStatus.overall_score >= 40 ? 'text-yellow-500' :
                        'text-destructive'
                      }`}>
                        {securityStatus.overall_score}%
                      </p>
                      <div className={`px-3 py-1 rounded-full text-sm font-mono font-bold border ${
                        securityStatus.grade?.startsWith('A') ? 'bg-primary/20 text-primary border-primary/30' :
                        securityStatus.grade?.startsWith('B') ? 'bg-accent/20 text-accent border-accent/30' :
                        securityStatus.grade?.startsWith('C') ? 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30' :
                        'bg-destructive/20 text-destructive border-destructive/30'
                      }`}>
                        {securityStatus.grade}
                      </div>
                    </div>
                  </div>
                  
                  {securityStatus.sections?.map((section, idx) => (
                    <div key={idx}>
                      <p className="text-sm text-muted-foreground font-mono mb-1">{section.name}</p>
                      <p className={`text-2xl font-mono font-bold ${
                        section.score >= 80 ? 'text-primary' :
                        section.score >= 60 ? 'text-accent' :
                        section.score >= 40 ? 'text-yellow-500' :
                        'text-destructive'
                      }`}>{section.score}%</p>
                    </div>
                  ))}
                </div>

                {/* Risk Summary */}
                <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border">
                  <span className="text-xs font-mono text-muted-foreground">
                    {securityStatus.risk_summary?.total || 0} risks assessed
                  </span>
                  <span className="text-xs font-mono text-primary">
                    {securityStatus.risk_summary?.mitigated || 0} mitigated
                  </span>
                  {securityStatus.risk_summary?.high > 0 && (
                    <span className="text-xs font-mono text-destructive">
                      {securityStatus.risk_summary.high} high risk
                    </span>
                  )}
                </div>
              </div>
              
              <button
                onClick={() => window.location.href = '/security-status'}
                className="px-6 py-3 bg-primary text-primary-foreground hover:bg-primary/90 rounded-md font-mono font-bold shadow-[0_0_15px_rgba(0,248,179,0.3)] transition-all text-sm"
                data-testid="view-full-report-button"
              >
                View Full Report →
              </button>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-card border border-border rounded-lg p-6 border-t-2 border-t-primary" data-testid="active-subscriptions-card">
            <div className="flex items-center justify-between mb-2">
              <Activity className="h-8 w-8 text-primary" />
              <div className="h-2 w-2 rounded-full bg-primary animate-pulse"></div>
            </div>
            <p className="text-3xl font-mono font-bold" data-testid="active-subscriptions-count">{activeSubs}</p>
            <p className="text-sm text-muted-foreground font-mono">Active Subscriptions</p>
          </div>

          <div className="bg-card border border-border rounded-lg p-6 border-t-2 border-t-accent" data-testid="open-tickets-card">
            <div className="flex items-center justify-between mb-2">
              <AlertCircle className="h-8 w-8 text-accent" />
              <div className="h-2 w-2 rounded-full bg-accent animate-pulse"></div>
            </div>
            <p className="text-3xl font-mono font-bold" data-testid="open-tickets-count">{openTickets}</p>
            <p className="text-sm text-muted-foreground font-mono">Open Tickets</p>
          </div>

          <div className="bg-card border border-border rounded-lg p-6 border-t-2 border-t-secondary" data-testid="total-tickets-card">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle2 className="h-8 w-8 text-secondary" />
            </div>
            <p className="text-3xl font-mono font-bold" data-testid="total-tickets-count">{tickets.length}</p>
            <p className="text-sm text-muted-foreground font-mono">Total Tickets</p>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-card border border-border rounded-lg p-6" data-testid="recent-activity-section">
          <h2 className="text-xl font-mono font-bold mb-4 flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            Recent Activity
          </h2>
          {tickets.length === 0 ? (
            <p className="text-muted-foreground text-center py-8" data-testid="no-activity-message">No recent activity</p>
          ) : (
            <div className="space-y-3">
              {tickets.slice(0, 5).map((ticket) => (
                <div key={ticket.ticket_id} className="flex items-center justify-between p-3 bg-background rounded-md border border-border" data-testid={`activity-item-${ticket.ticket_id}`}>
                  <div className="flex-1">
                    <p className="font-mono font-medium text-sm">{ticket.subject}</p>
                    <p className="text-xs text-muted-foreground font-mono mt-1">
                      {new Date(ticket.created_at).toLocaleDateString()} • {ticket.status}
                    </p>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-mono ${
                    ticket.status === 'open' ? 'bg-accent/10 text-accent' :
                    ticket.status === 'in_progress' ? 'bg-secondary/10 text-secondary' :
                    'bg-primary/10 text-primary'
                  }`}>
                    {ticket.status}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Account Manager Card */}
        {accountManager && (
          <div className="bg-gradient-to-r from-secondary/10 to-secondary/5 border border-secondary/20 rounded-lg p-6" data-testid="account-manager-card">
            <div className="flex items-start gap-4">
              {accountManager.profile_picture ? (
                <img 
                  src={accountManager.profile_picture} 
                  alt={accountManager.name}
                  className="h-20 w-20 rounded-full object-cover border-2 border-secondary/30"
                />
              ) : (
                <div className="h-20 w-20 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-mono font-bold text-secondary">
                    {(accountManager.name || 'AM').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                  </span>
                </div>
              )}
              <div className="flex-1">
                <p className="text-xs text-secondary font-mono uppercase tracking-wider mb-1">Your Account Manager</p>
                <h3 className="text-xl font-mono font-bold mb-1">{accountManager.name}</h3>
                {accountManager.job_title && (
                  <p className="text-sm text-muted-foreground mb-3">{accountManager.job_title}</p>
                )}
                <div className="flex flex-wrap gap-4">
                  {accountManager.email && (
                    <a href={`mailto:${accountManager.email}`} className="text-sm text-primary hover:underline flex items-center gap-1">
                      <Mail className="h-4 w-4" />
                      {accountManager.email}
                    </a>
                  )}
                  {accountManager.phone && (
                    <a href={`tel:${accountManager.phone}`} className="text-sm hover:text-primary flex items-center gap-1">
                      <Phone className="h-4 w-4" />
                      {accountManager.phone}
                    </a>
                  )}
                  {accountManager.linkedin_url && (
                    <a href={accountManager.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline flex items-center gap-1">
                      <Linkedin className="h-4 w-4" />
                      LinkedIn
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="quick-actions-section">
          <button
            onClick={() => window.location.href = '/tickets'}
            className="bg-card border border-primary/20 hover:bg-primary/5 rounded-lg p-6 text-left transition-colors"
            data-testid="view-tickets-action"
          >
            <AlertCircle className="h-8 w-8 text-primary mb-3" />
            <h3 className="font-mono font-bold mb-1">Support Tickets</h3>
            <p className="text-sm text-muted-foreground">Create or view your support tickets</p>
          </button>

          <button
            onClick={() => window.location.href = '/resources'}
            className="bg-card border border-accent/20 hover:bg-accent/5 rounded-lg p-6 text-left transition-colors"
            data-testid="view-resources-action"
          >
            <CheckCircle2 className="h-8 w-8 text-accent mb-3" />
            <h3 className="font-mono font-bold mb-1">Resources</h3>
            <p className="text-sm text-muted-foreground">Access documentation and guides</p>
          </button>
        </div>

        {/* Admin Quick Access Banner */}
        {user && user.role === 'admin' && (
          <div className="bg-gradient-to-r from-secondary/20 to-secondary/5 border border-secondary/20 rounded-lg p-6" data-testid="admin-access-banner">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-mono font-bold text-secondary mb-1 flex items-center gap-2">
                  🔐 ADMIN ACCESS
                </h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Manage customers, subscriptions, tickets, invoices & resources
                </p>
                <div className="flex flex-wrap gap-2 text-xs font-mono text-muted-foreground">
                  <span>• Allocate Subscriptions</span>
                  <span>• Answer Support Tickets</span>
                  <span>• Upload Resources</span>
                  <span>• Generate Invoices</span>
                </div>
              </div>
              <button
                onClick={() => window.location.href = '/admin/dashboard'}
                className="bg-secondary text-secondary-foreground hover:bg-secondary/90 px-6 py-3 rounded-md font-mono font-bold shadow-[0_0_15px_rgba(165,140,255,0.3)] transition-all"
              >
                Open Admin Portal →
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default CustomerDashboard;